import { isNil } from 'es-toolkit';

import type { CenterOfInfluenceRepSpecialty } from '@savvy/constants/generated/gql-enums';

import type { ReferralHouseholdTag } from '../detail/relationships/group-relationships';
import type {
  ContactWebGraph,
  ContactWebGraphEdge,
  HouseholdGraphNode,
  ProfessionalGraphNode,
  RelationshipKind,
} from './types';

type Endpoint = {
  readonly __typename: string;
  readonly id?: string;
  readonly name?: string;
  readonly salesforceAccountId?: string | null;
  readonly savvyAum?: string | null;
  readonly householdStage?: string | null;
  readonly advisor?: { readonly id: string; readonly name: string } | null;
  readonly householdTags?: readonly ReferralHouseholdTag[] | null;
};

type RelationshipEdge = {
  readonly id: string;
  readonly relationshipType: string;
  readonly referredOn: string | null;
  readonly createdAt: string;
};

type OutgoingEdge = RelationshipEdge & { readonly target: Endpoint };
type IncomingEdge = RelationshipEdge & { readonly source: Endpoint };

export type ContactWebRep = {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly photoUrl: string | null;
  readonly specialties: readonly CenterOfInfluenceRepSpecialty[];
  readonly outgoingProfessionalRelationships: readonly OutgoingEdge[];
  readonly incomingProfessionalRelationships: readonly IncomingEdge[];
};

function edgeKind(relationshipType: string): RelationshipKind | null {
  if (relationshipType === 'serves') return 'serves';
  if (relationshipType === 'referred' || relationshipType === 'referred_out') return 'refers';
  return null;
}

// Households and professional contacts are separate DB tables, each with its own raw
// integer id — the same numeric id can (and does, e.g. in seed data) refer to a household
// and a professional contact simultaneously. Namespace by kind so graph node ids stay
// unique; the raw id is preserved on ProfessionalGraphNode.repId for routing.
function householdNodeId(id: string): string {
  return `household:${id}`;
}

function professionalNodeId(id: string): string {
  return `professional:${id}`;
}

function endpointId(endpoint: Endpoint): string | null {
  if (endpoint.id === undefined) return null;
  return endpoint.__typename === 'Household' ? householdNodeId(endpoint.id) : professionalNodeId(endpoint.id);
}

function toHouseholdNode(endpoint: Endpoint): HouseholdGraphNode | null {
  if (endpoint.__typename !== 'Household' || endpoint.id === undefined || endpoint.name === undefined) return null;
  return {
    kind: 'household',
    id: householdNodeId(endpoint.id),
    name: endpoint.name,
    salesforceAccountId: endpoint.salesforceAccountId ?? null,
    savvyAum: endpoint.savvyAum ?? null,
    householdStage: endpoint.householdStage ?? null,
    advisorName: endpoint.advisor?.name ?? null,
    tags: endpoint.householdTags ?? [],
  };
}

function toProfessionalNode(rep: ContactWebRep): ProfessionalGraphNode {
  return {
    kind: 'professional',
    id: professionalNodeId(rep.id),
    repId: rep.id,
    name: rep.name,
    description: rep.description,
    photoUrl: rep.photoUrl,
    specialties: rep.specialties,
  };
}

/** Turns the fan-out of every contact's relationship edges into a deduped node/edge graph. */
export function buildContactWebGraph(reps: readonly ContactWebRep[]): ContactWebGraph {
  const householdNodes = new Map<string, HouseholdGraphNode>();
  const professionalNodes = new Map<string, ProfessionalGraphNode>();
  const edges = new Map<string, ContactWebGraphEdge>();

  for (const rep of reps) {
    const repNodeId = professionalNodeId(rep.id);
    professionalNodes.set(repNodeId, toProfessionalNode(rep));

    for (const edge of rep.outgoingProfessionalRelationships) {
      const targetId = endpointId(edge.target);
      const kind = edgeKind(edge.relationshipType);
      if (isNil(targetId) || isNil(kind)) continue;

      const household = toHouseholdNode(edge.target);
      if (!isNil(household)) householdNodes.set(household.id, household);

      edges.set(edge.id, {
        id: edge.id,
        source: repNodeId,
        target: targetId,
        kind,
        referredOn: edge.referredOn,
        createdAt: edge.createdAt,
      });
    }

    for (const edge of rep.incomingProfessionalRelationships) {
      const sourceId = endpointId(edge.source);
      const kind = edgeKind(edge.relationshipType);
      if (isNil(sourceId) || isNil(kind)) continue;

      const household = toHouseholdNode(edge.source);
      if (!isNil(household)) householdNodes.set(household.id, household);

      edges.set(edge.id, {
        id: edge.id,
        source: sourceId,
        target: repNodeId,
        kind,
        referredOn: edge.referredOn,
        createdAt: edge.createdAt,
      });
    }
  }

  return {
    nodes: [...householdNodes.values(), ...professionalNodes.values()],
    edges: [...edges.values()],
  };
}
